/* FeedbackToolbar.tsx — the single component you mount once, globally. */

import { useEffect } from 'react';
import { FeedbackProvider, useFeedback } from './store';
import { ControlBar } from './ControlBar';
import { CapturePanel } from './CapturePanel';
import { PinLayer } from './PinLayer';
import { ToastStack } from './ToastStack';
import { TicketDashboard } from './TicketDashboard';
import type { FeedbackToolbarProps } from './types';

function ToolbarShell({
  brand,
  offsetBody,
  onNavigate,
}: {
  brand: string;
  offsetBody: boolean;
  onNavigate?: (route: string) => void;
}) {
  const { fbOpen, setFbOpen, commentMode, setCommentMode, dashOpen, setDashOpen } = useFeedback();

  // Keep host content clear of the fixed top bar
  useEffect(() => {
    if (!offsetBody) return;
    document.body.classList.add('fbtk-offset');
    return () => document.body.classList.remove('fbtk-offset');
  }, [offsetBody]);

  // Global shortcuts: f capture · c comment · t dashboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) return;
      if (el?.closest?.('[role="textbox"], [contenteditable="true"]')) return;
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        setFbOpen(!fbOpen);
      } else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        setCommentMode(!commentMode);
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        setDashOpen(!dashOpen);
      } else if (e.key === 'Escape') {
        if (dashOpen) {
          e.preventDefault();
          setDashOpen(false);
        } else if (commentMode) {
          e.preventDefault();
          setCommentMode(false);
        } else if (fbOpen) {
          e.preventDefault();
          setFbOpen(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fbOpen, commentMode, dashOpen, setFbOpen, setCommentMode, setDashOpen]);

  return (
    <div className="fbtk-root">
      <ControlBar brand={brand} />
      <PinLayer />
      <CapturePanel />
      <ToastStack />
      {dashOpen && <TicketDashboard onNavigate={onNavigate} />}
    </div>
  );
}

export function FeedbackToolbar({
  route,
  routeLabel,
  syncUrl,
  screenshotUrl,
  authHeaders,
  isZoeAdmin,
  audienceKey,
  brand = 'Feedback',
  defaultAutoCapture = true,
  offsetBody = true,
  storageKey = 'fbtk',
  onNavigate,
}: FeedbackToolbarProps) {
  const resolvedRoute = route ?? window.location.pathname;
  const resolvedLabel = routeLabel ?? resolvedRoute;
  return (
    <FeedbackProvider
      route={resolvedRoute}
      routeLabel={resolvedLabel}
      syncUrl={syncUrl}
      authHeaders={authHeaders}
      defaultAutoCapture={defaultAutoCapture}
      storageKey={storageKey}
      screenshotUrl={screenshotUrl}
      isZoeAdmin={isZoeAdmin}
      audienceKey={audienceKey}
    >
      <ToolbarShell brand={brand} offsetBody={offsetBody} onNavigate={onNavigate} />
    </FeedbackProvider>
  );
}
